/**
 * Favorites API client for backend integration
 * Uses Orval-generated functions
 */

import {
	getFavoritesApiFavoritesGet,
	addToFavoritesApiFavoritesItemTypeItemIdPost,
	removeFromFavoritesApiFavoritesItemTypeItemIdDelete,
	reorderFavoritesApiFavoritesReorderPut
} from './generated/favorites/favorites';
import type {
	FavoritesList,
	FavoriteOperationResponse,
	FavoritesReorderRequest,
	FavoritesReorderResponse
} from './generated';
import { createApiRequestError } from '$lib/utils/apiErrors';

// Re-export types from generated schemas for convenience
export type {
	FavoritesList,
	FavoriteOperationResponse,
	FavoritesReorderResponse
} from './generated';
export type { FavoriteItemRead } from './generated/schemas/favoriteItemRead';

class FavoritesApiClient {
	/**
	 * Get user's favorites list
	 */
	async getFavorites(): Promise<FavoritesList> {
		return getFavoritesApiFavoritesGet();
	}

	/**
	 * Add item to favorites
	 */
	async addToFavorites(
		itemType: 'category' | 'tracker',
		itemId: string
	): Promise<FavoriteOperationResponse> {
		try {
			return await addToFavoritesApiFavoritesItemTypeItemIdPost(itemType, itemId);
		} catch {
			throw createApiRequestError('FAVORITE_REQUEST_FAILED');
		}
	}

	/**
	 * Remove item from favorites
	 */
	async removeFromFavorites(
		itemType: 'category' | 'tracker',
		itemId: string
	): Promise<FavoriteOperationResponse> {
		try {
			return await removeFromFavoritesApiFavoritesItemTypeItemIdDelete(itemType, itemId);
		} catch {
			throw createApiRequestError('FAVORITE_REQUEST_FAILED');
		}
	}

	/**
	 * Reorder favorites
	 */
	async reorderFavorites(
		orderedFavorites: Array<{ type: 'category' | 'tracker'; id: string }>
	): Promise<FavoritesReorderResponse> {
		const requestData: FavoritesReorderRequest = {
			ordered_favorites: orderedFavorites
		};

		try {
			return await reorderFavoritesApiFavoritesReorderPut(requestData);
		} catch {
			throw createApiRequestError('FAVORITE_REQUEST_FAILED');
		}
	}
}

// Export singleton instance
export const favoritesApi = new FavoritesApiClient();
